import { useCallback, useSyncExternalStore } from 'react';

/**
 * Rutas del panel con la History API del navegador, sin librería: el panel tiene pocas pantallas propias y cada
 * módulo aporta las suyas con la `path` de su entrada de menú (`getMyNavigation`).
 */
export const RUTAS_ACCESO = {
    entrar: '/entrar',
    dosPasos: '/entrar/dos-pasos',
    elegirNegocio: '/entrar/negocio',
    /** Soporte o Superadmin sin los dos pasos (G65, G123): «Activa la verificación en dos pasos». */
    activarDosPasos: '/entrar/activar-dos-pasos',
    olvidada: '/contrasena',
    enviada: '/contrasena/enviada',
    nueva: '/contrasena/nueva',
    cambiada: '/contrasena/cambiada',
    /** Enlace del email para verificarlo (`?token=`, 24 h, G134) o, sin enlace y sin verificar, «Revisa tu correo» (1.2). */
    verificarEmail: '/verificar-email',
    /** «Crear cuenta» (1.1): el alta de un propietario nuevo (tarea 490). */
    crearCuenta: '/crear-cuenta',
    /** «Protege tu cuenta» (1.6) en el alta, con sesión y sin negocio: tras «Email verificado». */
    protegerCuenta: '/entrar/proteger-cuenta',
    /** «Tu negocio» (1.5) en el alta, con sesión y sin negocio: tras «Protege tu cuenta». */
    crearNegocio: '/entrar/crear-negocio',
    /**
     * Contraseña corta (G1271, G1278): con `password_change_required` en `getMe`, cualquier ruta lleva aquí hasta que
     * `changePassword` responda bien. Con sesión, con negocio o sin él.
     */
    cambiarContrasena: '/entrar/cambiar-contrasena',
} as const;

/** Las rutas del alta que piden sesión sin negocio (1.6 y 1.5), como elegir negocio. */
export const RUTAS_ALTA_CON_SESION: readonly string[] = [RUTAS_ACCESO.protegerCuenta, RUTAS_ACCESO.crearNegocio];

/**
 * Rutas que no dependen de la sesión del panel: aceptar una invitación (1.10, el enlace del correo, con sesión o sin ella)
 * y la tablet del negocio (1.13, con su propia credencial de dispositivo).
 */
export const RUTA_INVITACION = '/invitacion';
export const RUTA_TABLET = '/tablet';

/**
 * Portal de partners (1.23): fuera de un negocio, con la sesión de la cuenta de partner (`account.type = partner`), que
 * entra por el mismo inicio de sesión y llega aquí (G133). Sus secciones van en `?seccion=`, como en el prototipo.
 */
export const RUTA_PORTAL_PARTNER = '/partner';
/**
 * Backoffice de plataforma (1.20 a 1.22): fuera de un negocio, con la sesión de una cuenta de plataforma
 * (`account.type = platform`: Superadmin o Soporte), que entra por el mismo inicio de sesión y llega aquí (G133). Sus
 * secciones van en `?seccion=` y la ficha de un negocio en `?negocio=`, como en el prototipo.
 */
export const RUTA_BACKOFFICE = '/backoffice';
/** Solicitud pública para ser partner (1.23, hoja roja con antibot): no depende de la sesión. */
export const RUTA_SOLICITUD_PARTNER = '/partners/solicitud';

/**
 * Páginas del cliente (G157, ADR-0058, `src/cliente/`), con su propia sesión de cliente: «Mis datos» (2.9, 2.10) y los
 * enlaces de un toque de los mensajes con su token (2.11). El back construye esos enlaces (`clientes.paginas_url`). Pagos
 * añade «Mis pagos» (4.10) y la página de pago (4.9, `/pagar/{business_id}/{link_code}`, el `payment_link_url`); Canales,
 * «Mis conversaciones» (11.10); Pases y Acceso, «Mis pases» (6.7); Documentos y Firma, la página para firmar (9.12,
 * `/firma/{business_id}/{token}`, la de `documentos.url_firma`) y «Mis documentos»; Citas, pedir cita (9.9,
 * `/pedir-cita/{slug}` desde la web y `/pedir-cita?clave=` desde el widget), «Mis citas» y el enlace de la cita (9.10,
 * `/cita/{business_id}/{token}`, el de `citas.url_enlace`); Reservas, la reserva pública (8.11 y 8.9, `/reservar/{slug}`
 * en la web o `/reservar?clave=…&marco=widget` en el widget) y «Mis reservas» (8.12); Eventos, la compra de entradas (10.13,
 * G901: `/comprar-entradas/{slug}` en la web o `/comprar-entradas?clave=…&marco=widget` en el widget).
 */
export const RUTAS_CLIENTE = { misDatos: '/mis-datos', misPagos: '/mis-pagos', misMensajes: '/mis-mensajes', misConversaciones: '/mis-conversaciones', misPases: '/mis-pases', misEntradas: '/mis-entradas', misReservas: '/mis-reservas', reservar: '/reservar', comprarEntradas: '/comprar-entradas', salirDeLaLista: '/salir-de-la-lista', misDocumentos: '/mis-documentos', firmar: '/firma', misCitas: '/mis-citas', pedirCita: '/pedir-cita', cita: '/cita', baja: '/baja', permiso: '/permiso', copia: '/copia', pagar: '/pagar' } as const;

/**
 * La página del pase (6.6): la dirección que lleva el QR, `/v1.<negocio>.<código>.<kid>.<firma>` (`pases.url_pase`, G50).
 * Empieza por `/v1.` (con punto), así que nunca se confunde con la API (`/v1/`).
 */
export const esRutaPase = (ruta: string): boolean => /^\/v1\.biz_[^/]+$/.test(ruta);

export function esRutaCliente(ruta: string): boolean {
    return esRutaPase(ruta) || ruta === RUTAS_CLIENTE.misPases || ruta === RUTAS_CLIENTE.misDatos || ruta === RUTAS_CLIENTE.misPagos || ruta === RUTAS_CLIENTE.misMensajes || ruta === RUTAS_CLIENTE.misConversaciones || ruta === RUTAS_CLIENTE.misEntradas || ruta === RUTAS_CLIENTE.misReservas || ruta === RUTAS_CLIENTE.reservar || ruta === RUTAS_CLIENTE.comprarEntradas || ruta === RUTAS_CLIENTE.misDocumentos || ruta === RUTAS_CLIENTE.misCitas || ruta === RUTAS_CLIENTE.pedirCita || [RUTAS_CLIENTE.baja, RUTAS_CLIENTE.permiso, RUTAS_CLIENTE.copia, RUTAS_CLIENTE.pagar, RUTAS_CLIENTE.salirDeLaLista, RUTAS_CLIENTE.firmar, RUTAS_CLIENTE.pedirCita, RUTAS_CLIENTE.cita, RUTAS_CLIENTE.reservar, RUTAS_CLIENTE.comprarEntradas].some((r) => ruta.startsWith(`${r}/`));
}

export function esRutaLibre(ruta: string): boolean {
    return ruta === RUTA_INVITACION || ruta === RUTA_SOLICITUD_PARTNER || ruta === RUTA_TABLET || ruta.startsWith(`${RUTA_TABLET}/`) || esRutaCliente(ruta);
}

export type RutaAcceso = (typeof RUTAS_ACCESO)[keyof typeof RUTAS_ACCESO];

export function esRutaAcceso(ruta: string): ruta is RutaAcceso {
    return (Object.values(RUTAS_ACCESO) as string[]).includes(ruta);
}

const EVENTO = 'reservaria:ruta';

function suscribir(aviso: () => void): () => void {
    window.addEventListener('popstate', aviso);
    window.addEventListener(EVENTO, aviso);
    return () => {
        window.removeEventListener('popstate', aviso);
        window.removeEventListener(EVENTO, aviso);
    };
}

const direccion = () => window.location.pathname + window.location.search;

export function navegar(destino: string, reemplazar = false): void {
    if (destino === direccion()) {
        return;
    }
    if (reemplazar) {
        window.history.replaceState(null, '', destino);
    } else {
        window.history.pushState(null, '', destino);
    }
    window.dispatchEvent(new Event(EVENTO));
}

export interface Ruta {
    ruta: string;
    busqueda: URLSearchParams;
    navegar: (destino: string, reemplazar?: boolean) => void;
}

/** Ruta actual. En el servidor (pruebas con renderToString) vale la que se le pase. */
export function useRuta(inicialServidor = '/'): Ruta {
    const actual = useSyncExternalStore(suscribir, direccion, () => inicialServidor);
    const ir = useCallback((destino: string, reemplazar = false) => {
        navegar(destino, reemplazar);
    }, []);
    const [ruta = '/', consulta = ''] = actual.split('?');
    return { ruta: ruta.replace(/\/+$/, '') || '/', busqueda: new URLSearchParams(consulta), navegar: ir };
}
